import type { ApiCreateScanNote } from '@kaiizen/kai-pro-api';

import type { ScanAudioAttachScope } from '../scanAudioScopeLib';
import type { VoiceScopeCommand } from './voiceScopeTypes';

/**
 * The scanPart(s) that should receive the provenance note for one voice apply.
 *
 * One note per task actually edited by the commands (deduped). Voice commands always target a
 * task, so this is normally a single note; the editor context (task, else group) is only a
 * fallback for the degenerate no-command case.
 */
export const resolveVoiceScopeScanNoteTargets = (
  commands: VoiceScopeCommand[],
  attachScope: ScanAudioAttachScope,
): string[] => {
  const fromCommands = [...new Set(commands.map(command => command.taskId))];
  if (fromCommands.length > 0) return fromCommands;

  const fallback = attachScope.taskId ?? attachScope.groupId;
  return fallback ? [fallback] : [];
};

/**
 * The provenance notes for one voice apply: the utterance, on each target from
 * `resolveVoiceScopeScanNoteTargets`. Dictated notes are internal - Kai never shares on a person's
 * behalf - and marked `agentCreated`, so the server keeps them internal even with no visibility sent.
 */
export const buildVoiceScopeScanNotes = (
  commands: VoiceScopeCommand[],
  utterance: string,
  attachScope: ScanAudioAttachScope,
): Pick<ApiCreateScanNote, 'scanPartId' | 'scanNoteType' | 'body' | 'visibility' | 'agentCreated'>[] => {
  const body = utterance.trim();
  if (!body) return [];
  return resolveVoiceScopeScanNoteTargets(commands, attachScope)
    .map(scanPartId => ({ scanPartId, scanNoteType: 'info', body, visibility: 'internal', agentCreated: true }));
};
